import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useSessionContext } from '../../contexts/SessionContext';
import SprintAPIService from '../../services/SprintAPIService';
import { supabase } from '../../lib/supabase';
import {
  Play,
  Pause,
  CheckCircle2,
  Circle,
  Clock,
  Target,
  AlertCircle,
  TrendingUp,
  Calendar,
  Zap,
  Settings,
  Undo,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import RecoveryPlanModal from './RecoveryPlanModal';
import EnhancedSprintBlock from '../Timeline/EnhancedSprintBlock';
import EnhancedSprintBlockV2 from '../Timeline/EnhancedSprintBlockV2';
import WorkloadCalculatorModal from '../Timeline/WorkloadCalculatorModal';

/**
 * SprintDashboard
 *
 * Main dashboard for the Sprint Planning & Execution System.
 * Shows preset sprints, today's schedule, progress tracking, and recovery options.
 * Integrates with existing SessionTracker for sprint execution.
 */
export default function SprintDashboard({ timelineItems = [], colleges = [] }) {
  const { currentUser } = useFamilyContext();
  const { startSession, activeSession } = useSessionContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [todaysSprints, setTodaysSprints] = useState([]);
  const [currentSprint, setCurrentSprint] = useState(null);
  const [progress, setProgress] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recoveryPlan, setRecoveryPlan] = useState(null);
  const [showCustomSprint, setShowCustomSprint] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [skippedSprints, setSkippedSprints] = useState([]);
  const [showSkipped, setShowSkipped] = useState(false);
  const [undoingSprintId, setUndoingSprintId] = useState(null);
  const [resetConfirmSprintId, setResetConfirmSprintId] = useState(null);

  // Load sprint data on mount and when user changes
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadSprintData = async () => {
      try {
        setLoading(true);

        // Load today's sprints
        const sprints = await SprintAPIService.getTodaysSprints(currentUser.id);
        setTodaysSprints(sprints || []);

        // Load current sprint (if any)
        const current = await SprintAPIService.getCurrentSprint(currentUser.id);
        setCurrentSprint(current);

        // Load progress
        const progressData = await SprintAPIService.getCurrentProgress(currentUser.id);
        setProgress(progressData);

        // Load alerts
        const alertsData = await SprintAPIService.getAlerts(currentUser.id, { unread: true });
        setAlerts(alertsData || []);

        // Load schedule
        const scheduleData = await SprintAPIService.getSchedule(currentUser.id);
        setSchedule(scheduleData);

        // Load skipped sprints (if any)
        if (scheduleData?.sprints) {
          const skipped = scheduleData.sprints.filter(s => s.status === 'SKIPPED');
          setSkippedSprints(skipped);
        }

      } catch (error) {
        console.error('[SprintDashboard] Error loading sprint data:', error);
        // If error is "no schedule", that's expected for first-time users
        if (error.message?.includes('No rows')) {
          console.log('[SprintDashboard] No sprint schedule found - user needs to be seeded');
        } else {
          toast.error('Failed to load sprint data');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSprintData();

    // Listen for sprint status changes from SessionTracker
    const handleSprintStatusChange = (event) => {
      console.log('[SprintDashboard] Sprint status changed, refreshing...', event.detail);
      loadSprintData();
    };

    window.addEventListener('sprintStatusChanged', handleSprintStatusChange);

    return () => {
      window.removeEventListener('sprintStatusChanged', handleSprintStatusChange);
    };
  }, [currentUser?.id]); // Only re-run when user ID changes

  // Get health status styling
  const getHealthStatusStyle = (status) => {
    switch (status) {
      case 'ON_TRACK':
        return {
          bg: 'bg-teal-100',
          text: 'text-teal-700',
          icon: 'text-teal-600',
          label: 'On Track'
        };
      case 'AT_RISK':
        return {
          bg: 'bg-gold-100',
          text: 'text-gold-700',
          icon: 'text-gold-600',
          label: 'At Risk'
        };
      case 'CRITICAL':
        return {
          bg: 'bg-coral-100',
          text: 'text-coral-700',
          icon: 'text-coral-600',
          label: 'Critical'
        };
      case 'EMERGENCY':
        return {
          bg: 'bg-red-100',
          text: 'text-red-700',
          icon: 'text-red-600',
          label: 'Emergency'
        };
      default:
        return {
          bg: 'bg-stone-100',
          text: 'text-stone-700',
          icon: 'text-stone-600',
          label: 'Unknown'
        };
    }
  };

  const healthStyle = progress ? getHealthStatusStyle(progress.risk_level) : getHealthStatusStyle('ON_TRACK');

  // Handle sprint start
  const handleStartSprint = async (sprint) => {
    try {
      // Start sprint in backend
      await SprintAPIService.startSprint(currentUser.id, sprint.id);

      // Prepare session data for SessionTracker
      const sessionData = {
        title: sprint.name,
        sprintId: sprint.id,
        userId: currentUser.id, // Add userId for pause/close functionality
        tasks: sprint.sprint_tasks.map(st => ({
          ...st.timeline_items,
          id: st.id, // Use sprint_task id
          taskId: st.task_id, // Keep reference to timeline_item
          estimated_duration: st.estimated_duration,
          isOptional: st.is_optional
        })),
        estimatedMinutes: sprint.estimated_duration,
        batchType: sprint.type
      };

      // Launch existing SessionTracker
      startSession(sessionData);

      // Reload sprint data
      const current = await SprintAPIService.getCurrentSprint(currentUser.id);
      setCurrentSprint(current);

      toast.success(`Started: ${sprint.name}`);
    } catch (error) {
      console.error('[SprintDashboard] Error starting sprint:', error);
      toast.error('Failed to start sprint');
    }
  };

  // Handle resuming a paused sprint
  const handleResumeSprint = async (sprint) => {
    try {
      await SprintAPIService.resumeSprint(currentUser.id, sprint.id);

      // Prepare session data for SessionTracker
      const sessionData = {
        title: sprint.name,
        sprintId: sprint.id,
        userId: currentUser.id, // Add userId for pause/close functionality
        tasks: sprint.sprint_tasks.map(st => ({
          ...st.timeline_items,
          id: st.id,
          taskId: st.task_id,
          estimated_duration: st.estimated_duration,
          isOptional: st.is_optional
        })),
        estimatedMinutes: sprint.estimated_duration,
        batchType: sprint.type
      };

      startSession(sessionData);

      const current = await SprintAPIService.getCurrentSprint(currentUser.id);
      setCurrentSprint(current);

      toast.success(`Resumed: ${sprint.name}`);
    } catch (error) {
      console.error('[SprintDashboard] Error resuming sprint:', error);
      toast.error('Failed to resume sprint');
    }
  };

  // Handle resetting a sprint (clears progress, resets to NOT_STARTED)
  const handleResetSprint = async (sprintId) => {
    try {
      // Reset sprint to NOT_STARTED and clear all task progress
      const { data: sprint, error } = await supabase
        .from('sprints')
        .update({
          status: 'NOT_STARTED',
          actual_start_time: null,
          actual_end_time: null,
          completion_percent: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', sprintId)
        .eq('user_id', currentUser.id)
        .select()
        .single();

      if (error) throw error;

      // Reset all sprint tasks to NOT_STARTED
      const { error: tasksError } = await supabase
        .from('sprint_tasks')
        .update({
          status: 'NOT_STARTED',
          completed_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('sprint_id', sprintId);

      if (tasksError) throw tasksError;

      // Reload data
      const sprints = await SprintAPIService.getTodaysSprints(currentUser.id);
      setTodaysSprints(sprints);

      const current = await SprintAPIService.getCurrentSprint(currentUser.id);
      setCurrentSprint(current);

      setResetConfirmSprintId(null);
      toast.success('Sprint reset - ready to start fresh!');
    } catch (error) {
      console.error('[SprintDashboard] Error resetting sprint:', error);
      toast.error('Failed to reset sprint');
    }
  };

  // Handle generating recovery plan
  const handleGenerateRecoveryPlan = async () => {
    try {
      const plan = await SprintAPIService.generateRecoveryPlan(currentUser.id);
      setRecoveryPlan(plan);
      setShowRecoveryModal(true);
      toast.success('Recovery plan generated');
    } catch (error) {
      console.error('[SprintDashboard] Error generating recovery plan:', error);
      toast.error('Failed to generate recovery plan');
    }
  };


  // Handle restoring a skipped sprint
  const handleRestoreSprint = async (sprintId) => {
    setUndoingSprintId(sprintId);
    try {
      // Find the recovery plan that skipped this sprint
      const adjustmentHistory = await SprintAPIService.getAdjustmentHistory(currentUser.id);
      const relevantAdjustment = adjustmentHistory.find(adj =>
        adj.affected_sprint_ids?.includes(sprintId) &&
        adj.reason && !adj.reason.includes('Undid recovery plan')
      );

      if (relevantAdjustment?.recovery_plan_id) {
        await SprintAPIService.undoRecoveryPlan(currentUser.id, relevantAdjustment.recovery_plan_id);
      } else {
        // If no recovery plan found, directly restore the sprint
        const { data, error } = await supabase
          .from('sprints')
          .update({
            status: 'NOT_STARTED',
            is_optional: false,
            can_be_skipped: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', sprintId)
          .select()
          .single();

        if (error) throw error;
      }

      // Reload data
      const scheduleData = await SprintAPIService.getSchedule(currentUser.id);
      setSchedule(scheduleData);
      if (scheduleData?.sprints) {
        const skipped = scheduleData.sprints.filter(s => s.status === 'SKIPPED');
        setSkippedSprints(skipped);
      }

      const progressData = await SprintAPIService.getCurrentProgress(currentUser.id);
      setProgress(progressData);

      toast.success('✅ Sprint restored to schedule');
    } catch (error) {
      console.error('[SprintDashboard] Error restoring sprint:', error);
      toast.error('Failed to restore sprint');
    } finally {
      setUndoingSprintId(null);
    }
  };

  // Get sprint type styling
  const getSprintTypeStyle = (type) => {
    switch (type) {
      case 'ADMIN':
        return { bg: 'bg-azure-100', text: 'text-azure-700', icon: '⚙️', label: 'Admin' };
      case 'RESEARCH':
        return { bg: 'bg-teal-100', text: 'text-teal-700', icon: '🔍', label: 'Research' };
      case 'ESSAY':
        return { bg: 'bg-gold-100', text: 'text-gold-700', icon: '✍️', label: 'Essay' };
      case 'SUBMISSION':
        return { bg: 'bg-coral-100', text: 'text-coral-700', icon: '🚀', label: 'Submit' };
      case 'MIXED':
        return { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: '📚', label: 'Mixed' };
      default:
        return { bg: 'bg-stone-100', text: 'text-stone-700', icon: '📋', label: type };
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <span className="ml-3 text-stone-600">Loading sprint schedule...</span>
        </div>
      </div>
    );
  }

  // If no schedule exists, show setup message
  if (!schedule && todaysSprints.length === 0) {
    return (
      <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg p-8">
        <div className="text-center">
          <Target className="w-12 h-12 text-slate-900 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sprint System Ready</h3>
          <p className="text-stone-600 mb-6">
            Your sprint schedule needs to be initialized. Contact your advisor to set up your personalized sprint plan.
          </p>
          <button
            onClick={() => setShowCustomSprint(true)}
            className="bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors"
          >
            Create Custom Sprint
          </button>
        </div>

        {/* Fallback: Custom Sprint Block */}
        {showCustomSprint && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-2 border-slate-900">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-900">Custom Sprint</h2>
                  <button
                    onClick={() => setShowCustomSprint(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
                <EnhancedSprintBlock timelineItems={timelineItems} colleges={colleges} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      {progress && (
        <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-white" />
              <h2 className="text-lg font-bold text-white">Sprint Progress</h2>
            </div>
            {progress.on_schedule ? (
              <div className="px-3 py-1 rounded-full text-sm font-bold bg-teal-500 text-white">
                On Track
              </div>
            ) : (
              <div className="px-3 py-1 rounded-full text-sm font-bold bg-coral-500 text-white">
                Timeline Adjusted
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Current Sprint Status */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm text-stone-600">Current Sprint</div>
                  <div className="text-xl font-bold text-slate-900">
                    Week {Math.ceil(progress.sprints_completed / 5) || 1}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-stone-600">
                    {(() => {
                      // Calculate nearest deadline from colleges prop
                      const today = new Date();
                      let nearestDeadline = null;
                      if (colleges && colleges.length > 0) {
                        colleges.forEach(college => {
                          if (college.application_round === 'early_decision' || college.application_round === 'early_action') {
                            // Estimate ED deadline (typically Oct 31 or Nov 1)
                            const edDeadline = new Date(today.getFullYear(), 9, 31); // Oct 31
                            if (edDeadline > today && (!nearestDeadline || edDeadline < nearestDeadline)) {
                              nearestDeadline = edDeadline;
                            }
                          }
                        });
                      }

                      if (nearestDeadline) {
                        const daysUntil = Math.ceil((nearestDeadline - today) / (1000 * 60 * 60 * 24));
                        return `ED Deadline`;
                      }
                      return 'Next Deadline';
                    })()}
                  </div>
                  <div className="text-xl font-bold text-coral-600">
                    {(() => {
                      const today = new Date();
                      let nearestDeadline = null;
                      if (colleges && colleges.length > 0) {
                        colleges.forEach(college => {
                          if (college.application_round === 'early_decision' || college.application_round === 'early_action') {
                            const edDeadline = new Date(today.getFullYear(), 9, 31);
                            if (edDeadline > today && (!nearestDeadline || edDeadline < nearestDeadline)) {
                              nearestDeadline = edDeadline;
                            }
                          }
                        });
                      }

                      if (nearestDeadline) {
                        const daysUntil = Math.ceil((nearestDeadline - today) / (1000 * 60 * 60 * 24));
                        return `${daysUntil} days`;
                      }
                      return '—';
                    })()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-stone-600">Today's Focus</div>
                  <div className="text-xl font-bold text-slate-900">
                    {todaysSprints.length > 0 ? `${todaysSprints.filter(s => s.status !== 'COMPLETE').length} / ${todaysSprints.length}` : '0 / 0'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-stone-600">Pace Needed</div>
                  <div className="text-xl font-bold text-teal-600">
                    {(() => {
                      const today = new Date();
                      let nearestDeadline = null;
                      if (colleges && colleges.length > 0) {
                        colleges.forEach(college => {
                          if (college.application_round === 'early_decision' || college.application_round === 'early_action') {
                            const edDeadline = new Date(today.getFullYear(), 9, 31);
                            if (edDeadline > today && (!nearestDeadline || edDeadline < nearestDeadline)) {
                              nearestDeadline = edDeadline;
                            }
                          }
                        });
                      }

                      if (nearestDeadline && progress.sprints_total > progress.sprints_completed) {
                        const daysUntil = Math.ceil((nearestDeadline - today) / (1000 * 60 * 60 * 24));
                        const remaining = progress.sprints_total - progress.sprints_completed;
                        const perDay = Math.ceil(remaining / daysUntil);
                        return `${perDay} per day`;
                      }
                      return '—';
                    })()}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, (progress.sprints_completed / progress.sprints_total) * 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-xs text-stone-600">
                <span>{progress.sprints_completed} of {progress.sprints_total} sprints complete</span>
                <span>{Math.round((progress.sprints_completed / progress.sprints_total) * 100)}%</span>
              </div>
            </div>

            {/* Timeline Adjustment Notice (instead of "Behind Schedule Warning") */}
            {!progress.on_schedule && (
              <div className="p-4 bg-coral-50 border-l-4 border-l-coral-500 rounded-lg">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-coral-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-bold text-coral-900 mb-1">
                      Timeline needs adjustment for ED deadlines
                    </div>
                    <div className="text-sm text-coral-700 mb-3">
                      Multiple strategies available to help you focus on top priorities. Choose what works best for you.
                    </div>
                    <button
                      onClick={handleGenerateRecoveryPlan}
                      className="bg-coral-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-coral-700 transition-colors"
                    >
                      View Strategy Options
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Today's Sprints */}
      <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden">
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-white" />
            <h2 className="text-lg font-bold text-white">Today's Sprints</h2>
          </div>
          <button
            onClick={() => setShowCustomSprint(true)}
            className="text-sm text-white hover:text-stone-300 flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Customize
          </button>
        </div>

        <div className="p-6 space-y-4">
          {todaysSprints.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto mb-3" />
              <p className="text-slate-900 font-medium mb-1">All sprints complete for today!</p>
              <p className="text-sm text-stone-600">Great work! Check back tomorrow.</p>
            </div>
          ) : (
            todaysSprints.map((sprint, index) => {
              const typeStyle = getSprintTypeStyle(sprint.type);
              // Sprint is only "active" if it matches currentSprint AND there's an active session in browser
              const isActive = currentSprint?.id === sprint.id && activeSession?.sprintId === sprint.id;
              const isComplete = sprint.status === 'COMPLETE';
              const isPaused = sprint.status === 'PAUSED';
              const isNotStarted = sprint.status === 'NOT_STARTED';

              return (
                <div
                  key={sprint.id}
                  className={`border-2 rounded-lg p-6 transition-all ${
                    isActive
                      ? 'border-teal-500 bg-teal-50'
                      : isComplete
                      ? 'border-stone-300 bg-stone-50 opacity-60'
                      : isPaused
                      ? 'border-gold-500 bg-gold-50'
                      : 'border-slate-900 bg-white hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Sprint Number */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <div className={`px-3 py-1 rounded-lg ${typeStyle.bg}`}>
                        <span className="text-xl">{typeStyle.icon}</span>
                      </div>
                    </div>

                    {/* Sprint Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-slate-900">
                            {sprint.name}
                          </h3>
                          {isPaused && (
                            <span className="px-2 py-0.5 bg-gold-100 text-gold-700 text-xs font-bold rounded-full">
                              Paused
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-stone-600">
                          <Clock className="w-4 h-4" />
                          <span>{sprint.estimated_duration} min</span>
                        </div>
                      </div>

                      {sprint.description && (
                        <p className="text-sm text-stone-600 mb-3">{sprint.description}</p>
                      )}

                      {/* Tasks Preview */}
                      {sprint.sprint_tasks && sprint.sprint_tasks.length > 0 && (
                        <div className="mb-3">
                          <div className="text-xs font-bold text-stone-600 mb-2">
                            {(() => {
                              const completedCount = sprint.sprint_tasks.filter(t => t.status === 'COMPLETE').length;
                              const totalCount = sprint.sprint_tasks.length;
                              if (completedCount === 0) {
                                return `${totalCount} Task${totalCount > 1 ? 's' : ''}:`;
                              }
                              return `${completedCount} of ${totalCount} Task${totalCount > 1 ? 's' : ''} Complete:`;
                            })()}
                          </div>
                          <div className="space-y-1">
                            {sprint.sprint_tasks.slice(0, 3).map((task) => (
                              <div key={task.id} className="flex items-center gap-2 text-sm">
                                {task.status === 'COMPLETE' ? (
                                  <CheckCircle2 className="w-4 h-4 text-teal-600" />
                                ) : (
                                  <Circle className="w-4 h-4 text-stone-400" />
                                )}
                                <span className={task.status === 'COMPLETE' ? 'text-stone-500 line-through' : 'text-stone-700'}>
                                  {task.timeline_items?.friendly_title || task.timeline_items?.title || 'Task'}
                                </span>
                              </div>
                            ))}
                            {sprint.sprint_tasks.length > 3 && (
                              <div className="text-xs text-stone-500 ml-6">
                                +{sprint.sprint_tasks.length - 3} more tasks
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Status & Actions */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {isComplete ? (
                          <div className="flex items-center gap-2 text-teal-600">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="text-sm font-bold">Complete</span>
                          </div>
                        ) : isActive ? (
                          <div className="flex items-center gap-2 text-teal-600">
                            <Zap className="w-5 h-5 animate-pulse" />
                            <span className="text-sm font-bold">In Progress</span>
                          </div>
                        ) : isPaused || (sprint.status === 'IN_PROGRESS' && !isActive) ? (
                          /* Show Resume/Reset for paused OR "stuck" in-progress sprints */
                          <>
                            <button
                              onClick={() => handleResumeSprint(sprint)}
                              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-800 transition-colors"
                            >
                              <Play className="w-4 h-4" />
                              Resume Sprint
                            </button>
                            {resetConfirmSprintId === sprint.id ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleResetSprint(sprint.id)}
                                  className="flex items-center gap-2 bg-coral-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-coral-700 transition-colors"
                                >
                                  Confirm Reset
                                </button>
                                <button
                                  onClick={() => setResetConfirmSprintId(null)}
                                  className="text-sm text-stone-600 hover:text-stone-900"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setResetConfirmSprintId(sprint.id)}
                                className="flex items-center gap-2 bg-white text-slate-900 border border-slate-900 px-3 py-2 rounded-lg text-sm font-bold hover:bg-slate-50 transition-colors"
                              >
                                <Settings className="w-4 h-4" />
                                Reset Progress
                              </button>
                            )}
                          </>
                        ) : isNotStarted ? (
                          activeSession ? (
                            /* If another sprint is active, show disabled button with message */
                            <div className="flex flex-col gap-1">
                              <button
                                disabled
                                className="flex items-center gap-2 bg-stone-300 text-stone-500 px-4 py-2 rounded-lg font-bold cursor-not-allowed"
                              >
                                <Play className="w-4 h-4" />
                                Start Sprint
                              </button>
                              <span className="text-xs text-stone-500">Finish current sprint first</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStartSprint(sprint)}
                              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-800 transition-colors"
                            >
                              <Play className="w-4 h-4" />
                              Start Sprint
                            </button>
                          )
                        ) : null}

                        {sprint.priority === 'CRITICAL' && (
                          <span className="px-2 py-1 bg-coral-100 text-coral-700 text-xs font-bold rounded">
                            Critical
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white border-2 border-coral-500 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-coral-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-white" />
              <h2 className="text-lg font-bold text-white">Alerts</h2>
            </div>
          </div>

          <div className="p-6 space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="p-4 bg-coral-50 border-l-4 border-coral-500 rounded"
              >
                <div className="font-bold text-coral-900 mb-1">{alert.title}</div>
                <div className="text-sm text-coral-700">{alert.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skipped Sprints */}
      {skippedSprints.length > 0 && (
        <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-stone-600 to-stone-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <EyeOff className="w-5 h-5 text-white" />
              <h2 className="text-lg font-bold text-white">Skipped Sprints</h2>
              <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-bold rounded-full">
                {skippedSprints.length}
              </span>
            </div>
            <button
              onClick={() => setShowSkipped(!showSkipped)}
              className="text-sm text-white hover:text-stone-200 flex items-center gap-2"
            >
              {showSkipped ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Show
                </>
              )}
            </button>
          </div>

          {showSkipped && (
            <div className="p-6 space-y-3">
              <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-700">
                  <span className="font-bold">These sprints were skipped by a recovery plan.</span> You can restore any of them to your schedule using the "Restore" button.
                </p>
              </div>

              {skippedSprints.map((sprint) => {
                const typeStyle = getSprintTypeStyle(sprint.type);

                return (
                  <div
                    key={sprint.id}
                    className="border-2 border-stone-300 rounded-lg p-6 bg-stone-50"
                  >
                    <div className="flex items-start gap-4">
                      {/* Sprint Type Icon */}
                      <div className={`px-3 py-2 rounded-lg ${typeStyle.bg} flex-shrink-0`}>
                        <span className="text-xl">{typeStyle.icon}</span>
                      </div>

                      {/* Sprint Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-bold text-slate-900">{sprint.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-stone-600">
                            <Clock className="w-4 h-4" />
                            <span>{sprint.estimated_duration} min</span>
                          </div>
                        </div>

                        {sprint.description && (
                          <p className="text-sm text-stone-600 mb-3">{sprint.description}</p>
                        )}

                        {/* Sprint Date */}
                        {sprint.date && (
                          <div className="text-xs text-stone-500 mb-3">
                            Originally scheduled: {new Date(sprint.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        )}

                        {/* Tasks Preview */}
                        {sprint.sprint_tasks && sprint.sprint_tasks.length > 0 && (
                          <div className="mb-3">
                            <div className="text-xs font-bold text-stone-600 mb-2">
                              {sprint.sprint_tasks.length} Task{sprint.sprint_tasks.length > 1 ? 's' : ''}:
                            </div>
                            <div className="space-y-1">
                              {sprint.sprint_tasks.slice(0, 3).map((task) => (
                                <div key={task.id} className="flex items-center gap-2 text-sm text-stone-600">
                                  <div className="w-1 h-1 rounded-full bg-stone-400"></div>
                                  <span>{task.timeline_items?.friendly_title || task.timeline_items?.title || 'Task'}</span>
                                </div>
                              ))}
                              {sprint.sprint_tasks.length > 3 && (
                                <div className="text-xs text-stone-500 ml-3">
                                  +{sprint.sprint_tasks.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Restore Button */}
                        <button
                          onClick={() => handleRestoreSprint(sprint.id)}
                          disabled={undoingSprintId === sprint.id}
                          className="mt-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {undoingSprintId === sprint.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Restoring...
                            </>
                          ) : (
                            <>
                              <Undo className="w-4 h-4" />
                              Restore to Schedule
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Custom Sprint Builder Modal */}
      <WorkloadCalculatorModal
        isOpen={showCustomSprint}
        onClose={() => setShowCustomSprint(false)}
        colleges={colleges}
        tasks={timelineItems}
        onStartSprint={(sprintData) => {
          startSession(sprintData);
          setShowCustomSprint(false);
        }}
      />

      {/* Recovery Plan Modal */}
      <RecoveryPlanModal
        isOpen={showRecoveryModal}
        onClose={() => {
          setShowRecoveryModal(false);
          setRecoveryPlan(null);
        }}
        userId={currentUser?.id}
        triggerAlertId={null}
        onPlanApplied={async (result) => {
          console.log('[SprintDashboard] Recovery plan applied, refreshing data...', result);

          // Reload all data after recovery plan is applied
          const sprints = await SprintAPIService.getTodaysSprints(currentUser.id);
          setTodaysSprints(sprints);

          const progressData = await SprintAPIService.getCurrentProgress(currentUser.id);
          setProgress(progressData);

          const scheduleData = await SprintAPIService.getSchedule(currentUser.id);
          setSchedule(scheduleData);
          if (scheduleData?.sprints) {
            const skipped = scheduleData.sprints.filter(s => s.status === 'SKIPPED');
            setSkippedSprints(skipped);
          }
        }}
      />
    </div>
  );
}
